
import { useState, useEffect } from 'react';
import { Bold, Italic, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Underline, Link as LinkIcon, Image, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import { useToast } from '@/hooks/use-toast';

interface RichTextEditorProps {
  initialValue: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export const RichTextEditor = ({ initialValue, onChange, placeholder = 'Enter content here...', minHeight = '300px' }: RichTextEditorProps) => {
  const [value, setValue] = useState(initialValue || '');
  const [currentTab, setCurrentTab] = useState('edit');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [altText, setAltText] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const insertMarkdown = (markdownBefore: string, markdownAfter: string = '') => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + markdownBefore + selectedText + markdownAfter + value.substring(end);
    
    setValue(newText);
    onChange(newText);
    
    // Set focus back to textarea and restore selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + markdownBefore.length,
        end + markdownBefore.length
      );
    }, 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    onChange(e.target.value);
  };

  const insertImage = () => {
    if (!imageUrl) {
      toast({
        title: "Missing information",
        description: "Please provide an image URL",
        variant: "destructive"
      });
      return;
    }

    const imgMarkdown = `![${altText || 'Image'}](${imageUrl})`;
    insertMarkdown(imgMarkdown, '\n\n');
    setImageUrl('');
    setAltText('');
  };

  const insertVideo = () => {
    if (!videoUrl) {
      toast({
        title: "Missing information",
        description: "Please provide a video URL",
        variant: "destructive"
      });
      return;
    }

    // Handle YouTube URLs
    let videoEmbed = '';
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      let videoId = '';
      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1].split('&')[0];
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1];
      }
      
      if (videoId) {
        videoEmbed = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
      }
    } else if (videoUrl.includes('vimeo.com')) {
      // Handle Vimeo URLs
      const vimeoId = videoUrl.split('vimeo.com/')[1];
      if (vimeoId) {
        videoEmbed = `<iframe src="https://player.vimeo.com/video/${vimeoId}" width="560" height="315" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
      }
    } else {
      // Generic video
      videoEmbed = `<video controls width="560">
  <source src="${videoUrl}" type="video/mp4">
  Your browser does not support the video tag.
</video>`;
    }
    
    insertMarkdown(videoEmbed, '\n\n');
    setVideoUrl('');
  };

  // Custom renderer components for the preview
  const MarkdownComponents = {
    img: (props: any) => (
      <img 
        src={props.src} 
        alt={props.alt} 
        className="max-w-full h-auto my-4 rounded-md" 
      />
    ),
  };

  return (
    <Card className="border rounded-lg overflow-hidden">
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <div className="bg-muted/50 border-b px-4 py-2 flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          
          {currentTab === 'edit' && (
            <div className="flex items-center gap-1 overflow-x-auto">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('# ', '\n')}
                title="Heading 1"
              >
                <Heading1 className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('## ', '\n')}
                title="Heading 2"
              >
                <Heading2 className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('**', '**')}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('*', '*')}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('__', '__')}
                title="Underline"
              >
                <Underline className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('[', '](https://example.com)')}
                title="Link"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('- ', '\n')}
                title="Bullet List"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => insertMarkdown('1. ', '\n')}
                title="Numbered List"
              >
                <ListOrdered className="h-4 w-4" />
              </Button>
              
              {/* Image Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    title="Insert Image"
                  >
                    <Image className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Insert Image</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Image URL</label>
                      <Input 
                        placeholder="https://example.com/image.jpg" 
                        value={imageUrl} 
                        onChange={(e) => setImageUrl(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Alt Text</label>
                      <Input 
                        placeholder="Description of the image" 
                        value={altText} 
                        onChange={(e) => setAltText(e.target.value)}
                      />
                    </div>
                    {imageUrl && (
                      <div className="border rounded-md p-2 mt-4">
                        <p className="text-sm font-medium mb-2">Preview:</p>
                        <img 
                          src={imageUrl} 
                          alt={altText || "Preview"} 
                          className="max-w-full h-auto max-h-40 object-contain" 
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "public/placeholder.svg";
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={insertImage}>Insert Image</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {/* Video Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    title="Insert Video"
                  >
                    <Video className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Insert Video</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Video URL</label>
                      <Input 
                        placeholder="YouTube, Vimeo, or direct video URL" 
                        value={videoUrl} 
                        onChange={(e) => setVideoUrl(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Supports YouTube, Vimeo, or direct video links (.mp4)
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={insertVideo}>Insert Video</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <TabsContent value="edit" className="p-0 m-0">
          <Textarea
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full border-0 rounded-none focus-visible:ring-0 resize-y"
            style={{ minHeight }}
          />
        </TabsContent>
        
        <TabsContent value="preview" className="p-4 m-0 prose max-w-none dark:prose-invert">
          {value ? (
            <div 
              className="markdown-content"
              dangerouslySetInnerHTML={{ 
                __html: value
                  // Allow HTML in markdown for videos
                  .replace(/<iframe.*?<\/iframe>/g, (match) => match)
                  .replace(/<video.*?<\/video>/g, (match) => match)
              }}
            />
          ) : (
            <p className="text-muted-foreground italic">No content to preview</p>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
};
